import { useCallback, useEffect, useRef, useState } from "react";

// Generic infinite-scroll data hook.
//   fetchPage(offset, limit) -> { items, total? , hasMore? }
//   resetKey: any value that, when it changes, resets the list (filters/search).
// Returns { items, total, loading, hasMore, error, loadMore, reload, setItems }.
export function useInfiniteScroll(fetchPage, resetKey, { limit = 50 } = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const genRef = useRef(0);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const gen = genRef.current;
    const offset = offsetRef.current;
    try {
      const res = await fetchRef.current(offset, limit);
      if (gen !== genRef.current) return; // a reset happened mid-flight
      const batch = res.items || [];
      setItems((prev) => (offset === 0 ? batch : [...prev, ...batch]));
      if (typeof res.total === "number") setTotal(res.total);
      const more = typeof res.hasMore === "boolean"
        ? res.hasMore
        : batch.length === limit
          && (typeof res.total === "number" ? offset + batch.length < res.total : true);
      hasMoreRef.current = more;
      setHasMore(more);
      offsetRef.current = offset + batch.length;
    } catch (e) {
      if (gen !== genRef.current) return;
      setError(e?.response?.data?.detail || "Failed to load");
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      if (gen === genRef.current) { loadingRef.current = false; setLoading(false); }
    }
  }, [limit]);

  const reset = useCallback(() => {
    genRef.current += 1;
    offsetRef.current = 0;
    loadingRef.current = false;
    hasMoreRef.current = true;
    setItems([]);
    setTotal(0);
    setHasMore(true);
    setError(null);
    setLoading(true);
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  return { items, total, loading, hasMore, error, loadMore, reload: reset, setItems };
}
